import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Phone } from 'lucide-react';
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { companyInfo } from '../../data/mock';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Chi Siamo', path: '/chi-siamo' },
    { name: 'Tende da Sole', path: '/tende-da-sole' },
    { name: 'Tende da Interno', path: '/tende-da-interno' },
    { name: 'Pergotende', path: '/pergotende' },
    { name: 'Bioclimatiche', path: '/bioclimatiche' },
    { name: 'Vetrate', path: '/vetrate-panoramiche' },
    { name: 'Installazioni', path: '/installazioni' },
    { name: 'Shop Online', path: '/shop' },
    { name: 'GT Smart', path: '/giovanni-tende-smart', highlight: true },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled
          ? 'bg-white/95 backdrop-blur-md shadow-sm py-4'
          : 'bg-transparent py-6'
      }`}
    >
      <div className="container mx-auto px-6 lg:px-12">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="relative z-10 flex items-center gap-3">
            <img 
              src={isScrolled ? "/assets/logo-nero.png" : "/assets/logo-bianco.png"}
              alt="Giovanni Tende Logo" 
              className="h-10 md:h-12 w-auto"
            />
            <span
              className={`text-xl md:text-2xl font-light tracking-wider transition-colors duration-300 ${
                isScrolled ? 'text-gray-900' : 'text-white'
              }`}
            >
              <span className="font-semibold">Giovanni</span> Tende
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm tracking-wide transition-all duration-300 hover:opacity-70 ${
                  link.highlight 
                    ? `px-3 py-1 rounded-full ${isScrolled ? 'bg-sky-500 text-white hover:bg-sky-600' : 'bg-white/20 text-white hover:bg-white/30'}`
                    : isScrolled ? 'text-gray-700' : 'text-white/90'
                } ${isActive(link.path) ? 'font-medium' : 'font-light'}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Button */}
          <div className="hidden lg:flex items-center gap-4">
            <a
              href={`tel:+39${companyInfo.phone.replace(/\s/g, '')}`}
              className={`flex items-center gap-2 text-sm transition-colors duration-300 ${
                isScrolled ? 'text-gray-600' : 'text-white/80'
              }`}
            >
              <Phone className="w-4 h-4" />
              <span className="font-light">{companyInfo.phone}</span>
            </a>
            <Link to="/preventivo">
              <Button
                className={`transition-all duration-300 ${
                  isScrolled
                    ? 'bg-sky-500 hover:bg-sky-600 text-white'
                    : 'bg-white text-gray-900 hover:bg-white/90'
                } px-6 py-2 text-sm font-medium rounded-none`}
              >
                Preventivo
              </Button>
            </Link>
          </div>

          {/* Mobile Menu */}
          <Sheet open={isOpen} onOpenChange={setIsOpen}>
            <SheetTrigger asChild className="lg:hidden">
              <button
                className={`p-2 transition-colors duration-300 ${
                  isScrolled ? 'text-gray-900' : 'text-white'
                }`}
              >
                {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="w-full sm:w-80 bg-white p-0">
              <SheetTitle className="sr-only">Menu di navigazione</SheetTitle>
              <div className="flex flex-col h-full">
                <div className="p-6 border-b border-gray-100">
                  <span className="text-xl font-light tracking-wider text-gray-900">
                    <span className="font-semibold">Giovanni</span> Tende
                  </span>
                </div>
                <nav className="flex-1 p-6">
                  <div className="flex flex-col gap-4">
                    {navLinks.map((link) => (
                      <Link
                        key={link.path}
                        to={link.path}
                        onClick={() => setIsOpen(false)}
                        className={`text-lg tracking-wide transition-colors duration-300 hover:text-sky-500 ${
                          isActive(link.path)
                            ? 'text-sky-500 font-medium'
                            : 'text-gray-700 font-light'
                        }`}
                      >
                        {link.name}
                      </Link>
                    ))}
                  </div>
                </nav>
                <div className="p-6 border-t border-gray-100 space-y-4">
                  <a
                    href={`tel:${companyInfo.phone}`}
                    className="flex items-center gap-3 text-gray-600"
                  >
                    <Phone className="w-5 h-5" />
                    <span>{companyInfo.phone}</span>
                  </a>
                  <Link to="/preventivo" onClick={() => setIsOpen(false)}>
                    <Button className="w-full bg-sky-500 hover:bg-sky-600 text-white py-3 rounded-none">
                      Richiedi Preventivo
                    </Button>
                  </Link>
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
};

export default Header;
